import React, { useEffect, useState, useCallback } from "react";
import "./MyReviews.scss";
import {
  theme,
  Layout,
  Spin,
  notification,
  Input,
  Pagination,
  Typography,
  Button,
  Rate,
  Image,
  Segmented,
  Select,
} from "antd";
import {
  ArrowLeftOutlined,
  SearchOutlined,
  LoadingOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import useWindowWidth from "../../hooks/useWindowWidth";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  getMyReviews,
  submitReview,
  removeReview,
} from "../../features/reviews/reviewsActions";
import { resetReviewsError } from "../../features/reviews/reviewsSlice";
import { CustomEmpty } from "../../components";
import { ConfirmModal } from "../../modals";
import { ReactComponent as StarIcon } from "../../assets/startIcon.svg";
import product_logo from "../../assets/logo_product.svg";
import classNames from "classnames";
import Utils from "../../utils";

const { Content } = Layout;
const { TextArea } = Input;

const PER_PAGE = 10;
const COMMENT_MAX_LENGTH = 1000;

// Server-side type filters: the value is the ?has_comment= value ("" = don't
// send the param). Plain toggles — the API carries no facet counts.
const FILTER_OPTIONS = [
  { label: "All", value: "" },
  { label: "With comment", value: "true" },
  { label: "Stars only", value: "false" },
];

// ?min_rating= steps; 0 = "Any" = don't send the param (Segmented has no
// deselect, so the cleared state is its own option).
const RATING_STEPS = [5, 4, 3, 2, 1];

// ?sort= — this endpoint accepts only "oldest"; newest first is the default,
// so clearing the select just drops the param (no "best" here — the
// min_rating chips already cover high ratings).
const SORT_OPTIONS = [{ label: "Oldest", value: "oldest" }];

const DRAFT_LABELS = [
  "Tap to rate",
  "Poor",
  "Fair",
  "Good",
  "Very good",
  "Excellent",
];

const doSearch = (value, cb) => {
  cb(value);
};
const debouncedSearch = Utils?.debounce(doSearch, 400);

export default function MyReviews() {
  const { useToken } = theme;
  const { token } = useToken();
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, title, text) => {
      api[type]({
        title: title,
        description: Utils.getErrorText(text),
        placement: "top",
      });
    },
    [api]
  );
  const { width, isMobile } = useWindowWidth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    loading,
    myReviews: reviews,
    myReviewsMeta: meta,
    submitting,
    error,
  } = useSelector((state) => state?.reviews);
  const dispatch = useDispatch();
  const [searchVal, setSearchVal] = useState(
    searchParams?.get("search") || ""
  );
  // Filters live in the URL like page/search, so a reload or a shared link
  // restores them and every change goes to the server.
  const typeFilter = searchParams?.get("has_comment") || "";
  const minRating = Number(searchParams?.get("min_rating")) || 0;
  const sortVal = searchParams?.get("sort") || "";
  const [editingId, setEditingId] = useState(null);
  const [draftRating, setDraftRating] = useState(0);
  const [draftComment, setDraftComment] = useState("");
  const [hoverStar, setHoverStar] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  // Item id awaiting the delete confirmation modal's verdict.
  const [deleteCandidateId, setDeleteCandidateId] = useState(null);

  const setUrlSearchParams = (newParams) => {
    const filteredSearchParams = Utils?.get_filtered_url_params(searchParams, {
      page: searchParams?.get("page") ? searchParams?.get("page") : undefined,
      per_page: searchParams?.get("per_page")
        ? searchParams?.get("per_page")
        : undefined,
      search: searchParams?.get("search")
        ? searchParams?.get("search")
        : undefined,
      has_comment: searchParams?.get("has_comment")
        ? searchParams?.get("has_comment")
        : undefined,
      min_rating: searchParams?.get("min_rating")
        ? searchParams?.get("min_rating")
        : undefined,
      sort: searchParams?.get("sort") ? searchParams?.get("sort") : undefined,
      ...newParams,
    });
    // Replace instead of push: the params only exist so a reload or a shared
    // link restores the list. Pushing them would stack one history entry per
    // keystroke/page, and the back arrow would crawl through them instead of
    // leaving the page.
    setSearchParams(filteredSearchParams, { replace: true });
  };

  useEffect(() => {
    dispatch(
      getMyReviews({
        page: searchParams?.get("page") || 1,
        per_page: searchParams?.get("per_page") || PER_PAGE,
        search: searchParams?.get("search") || undefined,
        has_comment: searchParams?.get("has_comment") || undefined,
        min_rating: searchParams?.get("min_rating") || undefined,
        sort: searchParams?.get("sort") || undefined,
      })
    );
    setSearchVal(searchParams?.get("search") || "");
  }, [dispatch, searchParams]);

  const onSearchInpChange = (e) => {
    const value = e?.target?.value;
    setSearchVal(value);
    debouncedSearch(value, () => {
      setUrlSearchParams({
        page: 1,
        per_page: PER_PAGE,
        search: value ? value : undefined,
      });
    });
  };

  const handlePagination = (e, a) => {
    Utils?.scrollIntoViewByClassName(".myReviews-cont");
    setUrlSearchParams({
      page: e,
      per_page: a,
    });
  };

  const refetchCurrentPage = () => {
    dispatch(
      getMyReviews({
        page: Number(searchParams?.get("page")) || 1,
        per_page: searchParams?.get("per_page") || PER_PAGE,
        search: searchParams?.get("search") || undefined,
        has_comment: searchParams?.get("has_comment") || undefined,
        min_rating: searchParams?.get("min_rating") || undefined,
        sort: searchParams?.get("sort") || undefined,
      })
    );
  };

  const startEditing = (review) => {
    setEditingId(review?.id);
    setDraftRating(review?.rating || 0);
    setDraftComment(review?.comment || "");
    setHoverStar(0);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setDraftRating(0);
    setDraftComment("");
    setHoverStar(0);
  };

  const handleSave = (review) => {
    // The upsert overwrites the customer's whole review of this item.
    dispatch(
      submitReview({
        menuItemId: review?.menu_item?.id,
        rating: draftRating,
        comment: draftComment,
      })
    )?.then((res) => {
      if (res?.type === "reviews/submit_item/fulfilled") {
        cancelEditing();
        refetchCurrentPage();
      }
    });
  };

  const handleDelete = (menuItemId) => {
    setDeletingId(menuItemId);
    dispatch(removeReview(menuItemId))?.then((res) => {
      setDeletingId(null);
      if (res?.type === "reviews/remove_item/fulfilled") {
        // The visible list changed — refetch the current page. When the last
        // row of a page > 1 was removed, step back a page instead.
        const page = Number(searchParams?.get("page")) || 1;
        const wasLastItemOnPage = reviews?.length === 1 && page > 1;
        if (wasLastItemOnPage) {
          setUrlSearchParams({
            page: page - 1,
          });
        } else {
          refetchCurrentPage();
        }
      }
    });
  };

  useEffect(() => {
    if (location?.pathname === "/reviews") {
      if (error && !loading) {
        for (let key in error) {
          openNotificationWithIcon("error", key, error[key]);
        }
        dispatch(resetReviewsError());
      }
    }
  }, [error, loading, location?.pathname, openNotificationWithIcon, dispatch]);

  const hasActiveNarrowing = Boolean(
    searchParams?.get("search") || typeFilter || minRating
  );

  // Shared by the desktop Segmented controls and their mobile Select twins.
  const ratingOptions = [
    { label: "Any", value: 0 },
    ...RATING_STEPS.map((step) => ({
      value: step,
      label: (
        <span className="myReviews-cont__ratingRow__option">
          <StarIcon
            width={13}
            height={13}
            style={{
              color: token?.Rate?.starColor,
            }}
          />
          {step === 5 ? "5 only" : `${step} & up`}
        </span>
      ),
    })),
  ];

  const onTypeFilterChange = (val) => {
    // Filter changes always land back on page 1.
    setUrlSearchParams({
      page: 1,
      has_comment: val ? val : undefined,
    });
  };

  const onMinRatingChange = (val) => {
    setUrlSearchParams({
      page: 1,
      min_rating: val ? val : undefined,
    });
  };

  const onSortChange = (val) => {
    setUrlSearchParams({
      page: 1,
      sort: val ? val : undefined,
    });
  };

  const isRecentlyEdited = (review) =>
    Math.abs(
      dayjs(review?.updated_at)?.valueOf() -
        dayjs(review?.created_at)?.valueOf()
    ) > 1000;

  return (
    <>
      {contextHolder}
      <Content
        className="myReviews-cont"
        style={{
          backgroundColor: token?.Layout?.contentBg,
        }}>
        <div className="myReviews-cont__header">
          <Button
            className="myReviews-cont__header__backBtn"
            type="text"
            size="small"
            aria-label="Go back"
            icon={
              <ArrowLeftOutlined
                style={{
                  fontSize: "24px",
                  color: token?.icons?.whiteIcon,
                }}
              />
            }
            onClick={() => {
              navigate(-1);
            }}
          />
          <Typography.Text
            className="myReviews-cont__header__title"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            My Reviews
          </Typography.Text>
        </div>

        <div className="myReviews-cont__filterRow">
          <Input
            className="myReviews-cont__filterRow__searchInp"
            size="large"
            placeholder="Search your reviews"
            prefix={<SearchOutlined />}
            allowClear
            onChange={onSearchInpChange}
            value={searchVal}
          />
          {!isMobile ? (
            <Segmented
              className="myReviews-cont__filterRow__segmented"
              // Same height as the large search input beside it.
              size="large"
              disabled={loading}
              options={FILTER_OPTIONS}
              value={typeFilter}
              onChange={onTypeFilterChange}
            />
          ) : null}
        </div>

        {!isMobile ? (
          <div className="myReviews-cont__ratingRow">
            <Segmented
              className="myReviews-cont__ratingRow__segmented"
              // Same height as the type-filter segmented above.
              size="large"
              disabled={loading}
              options={ratingOptions}
              value={minRating}
              onChange={onMinRatingChange}
            />
            <Select
              className="myReviews-cont__ratingRow__sortSelect"
              size="large"
              disabled={loading}
              placeholder="Sort"
              allowClear
              options={SORT_OPTIONS}
              // Unsorted (the default newest-first order) shows the
              // placeholder; clearing drops the param.
              value={sortVal ? sortVal : undefined}
              onChange={onSortChange}
            />
          </div>
        ) : (
          // From 900px down the segments become stacked selects with the
          // same options.
          <div className="myReviews-cont__mobileFilters">
            <Select
              className="myReviews-cont__mobileFilters__select"
              // Same height as the search box above.
              size="large"
              disabled={loading}
              options={FILTER_OPTIONS}
              value={typeFilter}
              onChange={onTypeFilterChange}
            />
            <Select
              className="myReviews-cont__mobileFilters__select"
              size="large"
              disabled={loading}
              options={ratingOptions}
              value={minRating}
              onChange={onMinRatingChange}
            />
            <Select
              className="myReviews-cont__mobileFilters__select"
              size="large"
              disabled={loading}
              placeholder="Sort"
              allowClear
              options={SORT_OPTIONS}
              value={sortVal ? sortVal : undefined}
              onChange={onSortChange}
            />
          </div>
        )}

        {/* With filters on, meta.count is the number of reviews matching
            the active filter. */}
        <div className="myReviews-cont__countWrapper">
          <Typography.Text
            className="myReviews-cont__countWrapper__number"
            style={{
              color: token?.Typography?.colorPrimaryText,
            }}>
            {meta?.count ? meta?.count : 0}
          </Typography.Text>
          <Typography.Text className="myReviews-cont__countWrapper__label">
            Reviews
          </Typography.Text>
        </div>

        <Spin
          size="large"
          spinning={loading}
          classNames={{
            root: classNames({
              "myReviews-cont__rows__spinWrapper": loading || !reviews?.length,
            }),
          }}>
          <div className="myReviews-cont__rows">
            {!reviews?.length && !loading ? (
              <CustomEmpty
                circleColor={
                  token?.icons?.emptyCircleIconColor
                    ? token?.icons?.emptyCircleIconColor
                    : ""
                }
                boxBorderColor={
                  token?.icons?.emptyIconBoxBorderColor
                    ? token?.icons?.emptyIconBoxBorderColor
                    : ""
                }
                description={
                  hasActiveNarrowing
                    ? "Nothing matches your search"
                    : "No reviews yet"
                }
              />
            ) : (
              reviews?.map((review) => {
                const item = review?.menu_item;
                const isAvailable = item?.is_available !== false;
                // can_review: false means the product was deleted from the
                // store — the review stays visible but read-only (editing
                // would 404). Deliberately independent of is_available:
                // sold-out/hidden items are still editable.
                const canReview = item?.can_review !== false;
                const isEditing = editingId === review?.id;
                return (
                  <div
                    key={review?.id}
                    className="myReviews-cont__rows__row"
                    style={{
                      backgroundColor: token?.Card?.colorBgCard,
                      opacity: isAvailable ? 1 : 0.6,
                    }}>
                    <div className="myReviews-cont__rows__row__imageWrapper">
                      <Image
                        alt={item?.name || "product"}
                        src={
                          item?.picture_url
                            ? item?.picture_url
                            : item?.image_url
                            ? item?.image_url
                            : product_logo
                        }
                        preview={true}
                        width="100%"
                        height="100%"
                        style={{
                          objectFit: "contain",
                        }}
                      />
                    </div>

                    <div className="myReviews-cont__rows__row__body">
                      <div className="myReviews-cont__rows__row__body__nameRow">
                        <Typography.Text
                          className={classNames(
                            "myReviews-cont__rows__row__body__nameRow__name",
                            {
                              "myReviews-cont__rows__row__body__nameRow__name--link":
                                isAvailable,
                            }
                          )}
                          style={{
                            color: token?.Typography?.mainColorText,
                          }}
                          // Tooltip appears only when the name is actually
                          // truncated — same pattern as ProductCard.
                          ellipsis={{
                            tooltip: {
                              title: item?.name || "",
                            },
                          }}
                          onClick={() => {
                            if (isAvailable) {
                              navigate(`/product/${item?.id}`, {
                                state: item,
                              });
                              Utils?.scrollIntoViewByClassName(
                                ".appHeader-cont"
                              );
                            }
                          }}>
                          {item?.name || ""}
                        </Typography.Text>
                        {!isAvailable ? (
                          <span className="myReviews-cont__rows__row__body__nameRow__unavailable">
                            Unavailable
                          </span>
                        ) : null}
                      </div>

                      {!isEditing ? (
                        <div className="myReviews-cont__rows__row__body__view">
                          <div className="myReviews-cont__rows__row__body__view__rateRow">
                            <Rate
                              disabled
                              value={review?.rating ? review?.rating : 0}
                              character={<StarIcon width={14} height={14} />}
                              count={5}
                              style={{
                                fontSize: "12px",
                              }}
                            />
                            <Typography.Text
                              className="myReviews-cont__rows__row__body__view__rateRow__rating"
                              style={{
                                color: token?.Typography?.colorPrimaryText,
                              }}>
                              {review?.rating
                                ? Number(review?.rating)?.toFixed(1)
                                : "0"}
                            </Typography.Text>
                            <span className="myReviews-cont__rows__row__body__view__rateRow__dot" />
                            <Typography.Text className="myReviews-cont__rows__row__body__view__rateRow__date">
                              {review?.updated_at
                                ? dayjs(review?.updated_at)?.format(
                                    "MMM D, YYYY"
                                  )
                                : ""}
                            </Typography.Text>
                            {isRecentlyEdited(review) ? (
                              <Typography.Text className="myReviews-cont__rows__row__body__view__rateRow__edited">
                                edited
                              </Typography.Text>
                            ) : null}
                          </div>
                          {review?.comment ? (
                            <Typography.Text
                              className="myReviews-cont__rows__row__body__view__comment"
                              style={{
                                color:
                                  "color-mix(in srgb, var(--text-main, #fff) 85%, transparent)",
                              }}>
                              {review?.comment}
                            </Typography.Text>
                          ) : (
                            <div className="myReviews-cont__rows__row__body__view__noComment">
                              Rated only — no comment yet
                              {canReview ? (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="myReviews-cont__rows__row__body__view__noComment__addLink"
                                  style={{
                                    color: token?.Typography?.colorPrimaryText,
                                  }}
                                  onClick={() => {
                                    startEditing(review);
                                  }}>
                                  Add one
                                </span>
                              ) : null}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="myReviews-cont__rows__row__body__editor">
                          <div className="myReviews-cont__rows__row__body__editor__rateRow">
                            <Rate
                              // Input is whole stars only — halves exist
                              // only in displayed averages.
                              value={draftRating}
                              disabled={submitting}
                              character={<StarIcon width={24} height={24} />}
                              count={5}
                              onChange={(val) => {
                                setDraftRating(val);
                              }}
                              onHoverChange={(val) => {
                                setHoverStar(val || 0);
                              }}
                              style={{
                                fontSize: "16px",
                              }}
                            />
                            <Typography.Text className="myReviews-cont__rows__row__body__editor__rateRow__label">
                              {DRAFT_LABELS[hoverStar || draftRating]}
                            </Typography.Text>
                          </div>
                          <TextArea
                            className="myReviews-cont__rows__row__body__editor__textArea"
                            placeholder="Share what you thought about this product"
                            autoSize={{ minRows: 3, maxRows: 6 }}
                            maxLength={COMMENT_MAX_LENGTH}
                            value={draftComment}
                            onChange={(e) => {
                              setDraftComment(e?.target?.value);
                            }}
                          />
                          <div className="myReviews-cont__rows__row__body__editor__btnRow">
                            <Button
                              type="primary"
                              size="small"
                              loading={submitting}
                              disabled={!draftRating}
                              onClick={() => {
                                handleSave(review);
                              }}>
                              Save changes
                            </Button>
                            <Button
                              size="small"
                              disabled={submitting}
                              onClick={cancelEditing}>
                              Cancel
                            </Button>
                            <span className="myReviews-cont__rows__row__body__editor__btnRow__counter">
                              {draftComment?.length}/{COMMENT_MAX_LENGTH}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {!isEditing ? (
                      <div className="myReviews-cont__rows__row__actions">
                        {/* Deleting your own review of a deleted product
                            still works, so the Delete button stays. */}
                        {canReview ? (
                          <Button
                            size="small"
                            type="text"
                            className="myReviews-cont__rows__row__actions__editBtn"
                            aria-label="Edit review"
                            disabled={Boolean(deletingId)}
                            icon={
                              <EditOutlined
                                style={{
                                  color: token?.Typography?.colorPrimaryText,
                                }}
                              />
                            }
                            onClick={() => {
                              startEditing(review);
                            }}
                          />
                        ) : null}
                        <Button
                          size="small"
                          type="text"
                          loading={
                            deletingId === item?.id && {
                              // Brand-colored spinner, same as the heart
                              // button's inline loading.
                              icon: (
                                <LoadingOutlined
                                  spin
                                  style={{
                                    color: token?.icons?.colorPrimaryIcon,
                                  }}
                                />
                              ),
                            }
                          }
                          disabled={
                            Boolean(deletingId) && deletingId !== item?.id
                          }
                          aria-label="Delete review"
                          icon={
                            <DeleteOutlined
                              style={{
                                color: token?.colorError,
                              }}
                            />
                          }
                          onClick={() => {
                            setDeleteCandidateId(item?.id);
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </Spin>
        <Pagination
          className="myReviews-cont__pagination"
          disabled={loading}
          pageSize={searchParams?.get("per_page") || PER_PAGE}
          current={searchParams?.get("page") || 1}
          defaultCurrent={searchParams?.get("page") || 1}
          total={meta?.count ? meta?.count : ""}
          responsive={true}
          onChange={handlePagination}
          showQuickJumper={width < 584 && width >= 577 ? false : true}
          showSizeChanger={false}
        />
        <ConfirmModal
          isOpened={Boolean(deleteCandidateId)}
          title={"Are you sure"}
          subTitle={"You want to delete this review ?"}
          onCancel={() => {
            setDeleteCandidateId(null);
          }}
          onOk={() => {
            handleDelete(deleteCandidateId);
            setDeleteCandidateId(null);
          }}
          token={token}
        />
      </Content>
    </>
  );
}
